// X223 (R92-M1): fixed controls at the foot of a phone screen (the club home's Setup guide bar) tell the assistant's
// phone coach mark how much room they take, so it stacks ABOVE them and never covers one; a panel that opens over the
// page from one of them (the expanded guide) pauses the coach mark until it closes.
//
// rooms:  id -> px from the viewport's bottom edge to the control's top (its own gap and the safe area included)
// covers: id -> true while that control has something open over the page
import {computed, nextTick, onBeforeUnmount, onMounted, watch} from 'vue';

export const footRoomFromRect = (rect, viewportHeight) => {
  if (!rect || !(rect.height > 0) || !(viewportHeight > 0)) return 0;
  return Math.max(0, Math.ceil(viewportHeight - rect.top));
};

export const useFootControls = () => {
  const state = useState('tl-foot-controls', () => ({rooms: {}, covers: {}}));

  const setRoom = (id, px) => {
    const next = px > 0 ? Math.ceil(px) : 0;
    if ((state.value.rooms[id] ?? 0) === next) return;
    state.value = {...state.value, rooms: {...state.value.rooms, [id]: next}};
  };
  const setCover = (id, on) => {
    if (!!state.value.covers[id] === !!on) return;
    state.value = {...state.value, covers: {...state.value.covers, [id]: !!on}};
  };
  const release = (id) => {
    const rooms = {...state.value.rooms};
    const covers = {...state.value.covers};
    delete rooms[id];
    delete covers[id];
    state.value = {rooms, covers};
  };

  const footRoom = computed(() => Math.max(0, ...Object.values(state.value.rooms)));
  const footCovered = computed(() => Object.values(state.value.covers).some(Boolean));

  return {footRoom, footCovered, setRoom, setCover, release};
};

// X224 (R93-M1): an in-page foot control that is not position: fixed (the setup assistant's composer foot, its floating
// "Your draft" bar) registers its room the same way: measured from its box, again on its own resize, a window resize,
// and a resize of anything `alsoObserve(el)` returns (the box it sits in, when it moves without changing size). 0 while
// the element is gone (v-if), released on unmount.
export const useFootRoom = (id, elRef, {alsoObserve = null} = {}) => {
  const {setRoom, release} = useFootControls();
  let observer = null;
  const measure = () => {
    if (typeof window === 'undefined') return;
    const el = elRef.value;
    setRoom(id, el?.getBoundingClientRect ? footRoomFromRect(el.getBoundingClientRect(), window.innerHeight) : 0);
  };
  const observe = () => {
    observer?.disconnect();
    observer = null;
    const el = elRef.value;
    if (el?.getBoundingClientRect && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(el);
      const extra = alsoObserve?.(el);
      if (extra) observer.observe(extra);
    }
    measure();
  };
  watch(elRef, () => nextTick(observe), {flush: 'post'});
  onMounted(() => {
    window.addEventListener('resize', measure);
    observe();
  });
  onBeforeUnmount(() => {
    window.removeEventListener('resize', measure);
    observer?.disconnect();
    release(id);
  });
  return {measure};
};
